import { X, Plus, Trash2, ChevronDown } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getAllRole } from "@/api/role";
import { getUsersWithRoles, UserWithRoleType } from "@/api/role-user";
import toast from "react-hot-toast";

import { RoleType } from "@/types/role";
import {
  AccessRule,
  AccessAction,
  AccessRuleMode,
  AccessRuleStatus,
  TableAccessConfig
} from "@/types/table-access";

import { TableAccessManager } from "@/utils/table-access-manager";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const togglePrimitive = <T extends string | number>(
  items: T[],
  item: T
): T[] => {
  return items.includes(item)
    ? items.filter((x) => x !== item)
    : [...items, item];
};

const normalizeText = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

interface TableAccessModalProps {
  isOpen: boolean;
  tableId: string;
  table: HTMLElement | null;
  onClose: () => void;
  availableColumns?: string[];
  columnsTitle?: string;
  defaultRuleColumns?: string[];
  title?: string;
  showColumns?: boolean;
  includeWildcard?: boolean;
  wildcardLabel?: string;
}

/**
 * Centralizes access-rule editing so multiple element types share the same logic.
 */
export const TableAccessModal: React.FC<TableAccessModalProps> = ({
  isOpen,
  tableId,
  table,
  onClose,
  availableColumns,
  columnsTitle,
  defaultRuleColumns,
  title,
  showColumns = true,
  includeWildcard = false,
  wildcardLabel
}) => {
  const shouldShowColumns = showColumns !== false;
  const shouldIncludeWildcard = includeWildcard === true;
  const resolvedWildcardLabel = wildcardLabel ?? "کل جدول";

  const normalizeConfig = useCallback(
    (nextConfig: TableAccessConfig) => {
      if (!shouldShowColumns) {
        return {
          ...nextConfig,
          rules: nextConfig.rules.map((rule) => ({ ...rule, columns: [] }))
        };
      }

      if (!defaultRuleColumns || defaultRuleColumns.length === 0) {
        return nextConfig;
      }

      return {
        ...nextConfig,
        rules: nextConfig.rules.map((rule) =>
          rule.columns.length > 0
            ? rule
            : { ...rule, columns: [...defaultRuleColumns] }
        )
      };
    },
    [defaultRuleColumns, shouldShowColumns]
  );

  const [config, setConfig] = useState<TableAccessConfig>(() => {
    if (!table) return TableAccessManager.createConfig(tableId);

    const existingData = table.getAttribute("data-access-control");
    const parsed = TableAccessManager.deserialize(existingData);
    const nextConfig = parsed
      ? { ...parsed, tableId }
      : TableAccessManager.createConfig(tableId);
    return normalizeConfig(nextConfig);
  });

  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleType[]>([]);
  const [users, setUsers] = useState<UserWithRoleType[]>([]);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [columnSearch, setColumnSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const existingData = table?.getAttribute("data-access-control") ?? null;

    const parsed = TableAccessManager.deserialize(existingData);
    const nextConfig = parsed
      ? { ...parsed, tableId }
      : TableAccessManager.createConfig(tableId);
    setConfig(normalizeConfig(nextConfig));
    setExpandedRuleId(null);
    setRoleSearch("");
    setUserSearch("");
    setColumnSearch("");
  }, [
    isOpen,
    table,
    tableId,
    defaultRuleColumns,
    shouldShowColumns,
    normalizeConfig
  ]);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const loadRoles = async () => {
      setIsRolesLoading(true);
      try {
        const response = await getAllRole({ page: 1, size: 1000 });
        if (!isCancelled) {
          setRoles(response?.data ?? []);
        }
      } catch {
        if (!isCancelled) {
          setRoles([]);
          toast.error("دریافت لیست نقش‌ها ناموفق بود");
        }
      } finally {
        if (!isCancelled) {
          setIsRolesLoading(false);
        }
      }
    };

    loadRoles();

    return () => {
      isCancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const loadUsers = async () => {
      setIsUsersLoading(true);
      try {
        const response = await getUsersWithRoles({
          pageSize: 1000,
          pageNumber: 1,
          search: userSearch.trim() || undefined
        });
        if (!isCancelled) {
          setUsers(response?.data ?? []);
        }
      } catch {
        if (!isCancelled) {
          setUsers([]);
          toast.error("دریافت لیست کاربران ناموفق بود");
        }
      } finally {
        if (!isCancelled) {
          setIsUsersLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, userSearch]);

  const resolvedColumns = useMemo((): string[] => {
    if (!shouldShowColumns) return [];
    if (availableColumns) return availableColumns;
    if (!table) return [];

    const dbColumns: string[] = [];
    const dataConditionRaw = table.getAttribute("data-condition") ?? "";
    const braceMatches = Array.from(
      dataConditionRaw.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)
    );

    if (braceMatches.length > 0) {
      braceMatches.forEach((m) => {
        const col = normalizeText(m[1] ?? "");
        if (col) dbColumns.push(col);
      });
    } else {
      dataConditionRaw
        .split(/\s+/)
        .map((token) => normalizeText(token))
        .filter(Boolean)
        .forEach((token) => dbColumns.push(token));
    }

    const manualHeaderColumns = Array.from(
      table.querySelectorAll("thead tr:first-child th, thead tr:first-child td")
    )
      .map((cell) => normalizeText(cell.textContent ?? ""))
      .filter((text) => text.length > 0 && text !== "عنوان جدول");

    const unique = new Set<string>();
    const ordered: string[] = [];
    [...dbColumns, ...manualHeaderColumns].forEach((col) => {
      if (unique.has(col)) return;
      unique.add(col);
      ordered.push(col);
    });

    if (!shouldIncludeWildcard) return ordered;

    return ["*", ...ordered.filter((col) => col !== "*")];
  }, [availableColumns, table, shouldShowColumns, shouldIncludeWildcard]);

  const resolveColumnLabel = useCallback(
    (column: string) => {
      if (column === "*") return resolvedWildcardLabel;
      return column;
    },
    [resolvedWildcardLabel]
  );

  const filteredRoles = useMemo(() => {
    const query = normalizeText(roleSearch).toLowerCase();
    if (!query) return roles;

    return roles.filter((role) => {
      const labelFa = role.displayNameFa ?? role.nameFa;
      const labelEn = role.displayNameEn ?? role.nameEn;
      const haystack = `${labelFa} ${labelEn}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [roles, roleSearch]);

  const filteredColumns = useMemo(() => {
    const query = normalizeText(columnSearch).toLowerCase();
    if (!query) return resolvedColumns;
    return resolvedColumns.filter((col) => col.toLowerCase().includes(query));
  }, [resolvedColumns, columnSearch]);

  const resolveUserLabel = useCallback((user: UserWithRoleType) => {
    return user.fullName?.trim() || user.userName || String(user.userId);
  }, []);

  const applyTargetLabel = shouldShowColumns ? "ستون‌های انتخاب‌شده" : "المان";

  /**
   * Save configuration to table data attribute
   */
  const saveConfiguration = useCallback(() => {
    if (!table) return;

    const serialized = TableAccessManager.serialize(config);
    table.setAttribute("data-access-control", serialized);
    toast.success("تنظیمات دسترسی ذخیره شد");
    onClose();
  }, [config, table, onClose]);

  /**
   * Add new rule
   */
  const handleAddRule = useCallback(() => {
    setConfig((prev) => {
      const baseRule = TableAccessManager.createRule(
        `قانون ${prev.rules.length + 1}`
      );
      const newRule =
        defaultRuleColumns && defaultRuleColumns.length > 0
          ? { ...baseRule, columns: [...defaultRuleColumns] }
          : baseRule;
      setExpandedRuleId(newRule.id);
      return TableAccessManager.addRule(prev, newRule);
    });
  }, [defaultRuleColumns]);

  /**
   * Delete rule
   */
  const handleDeleteRule = useCallback((ruleId: string) => {
    setConfig((prev) => TableAccessManager.removeRule(prev, ruleId));
    setExpandedRuleId((prev) => (prev === ruleId ? null : prev));
    toast.success("قانون حذف شد");
  }, []);

  /**
   * Update rule basic properties
   */
  const handleUpdateRule = useCallback(
    (ruleId: string, updates: Partial<AccessRule>) => {
      setConfig((prev) => TableAccessManager.updateRule(prev, ruleId, updates));
    },
    []
  );

  const toggleRoleForRule = useCallback((ruleId: string, roleId: number) => {
    setConfig((prev) => {
      const rule = TableAccessManager.getRule(prev, ruleId);
      if (!rule) return prev;

      return TableAccessManager.updateRule(prev, ruleId, {
        allRoles: false,
        roleIds: togglePrimitive(rule.roleIds, roleId)
      });
    });
  }, []);

  const toggleColumnForRule = useCallback((ruleId: string, column: string) => {
    setConfig((prev) => {
      const rule = TableAccessManager.getRule(prev, ruleId);
      if (!rule) return prev;

      return TableAccessManager.updateRule(prev, ruleId, {
        columns: togglePrimitive(rule.columns, column)
      });
    });
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-right text-lg font-bold">
            {title ?? "مدیریت دسترسی جدول"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Add Rule Button */}
          <Button
            onClick={handleAddRule}
            variant="outline"
            className="w-full gap-2"
          >
            <Plus size={18} />
            <span>افزودن قانون جدید</span>
          </Button>

          {/* Rules List */}
          <div className="space-y-2">
            {config.rules.map((rule) => (
              <div key={rule.id} className="rounded-lg border p-4">
                {/* Rule Header */}
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex flex-1 items-center gap-3">
                    <button
                      onClick={() =>
                        setExpandedRuleId(
                          expandedRuleId === rule.id ? null : rule.id
                        )
                      }
                      className="rounded p-1 hover:bg-gray-100"
                    >
                      <ChevronDown
                        size={20}
                        className={`transition-transform ${
                          expandedRuleId === rule.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <div className="flex-1">
                      <Input
                        value={rule.name}
                        onChange={(e) =>
                          handleUpdateRule(rule.id, { name: e.target.value })
                        }
                        className="text-right font-semibold"
                        placeholder="نام قانون"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteRule(rule.id)}
                    className="rounded p-2 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {/* Expanded Details */}
                {expandedRuleId === rule.id && (
                  <div className="space-y-4 border-t pt-4">
                    {/* Status Toggle */}
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">وضعیت:</label>
                      <select
                        value={
                          rule.status === AccessRuleStatus.ACTIVE
                            ? "active"
                            : rule.status === AccessRuleStatus.INACTIVE
                              ? "inactive"
                              : "export-share"
                        }
                        onChange={(e) =>
                          handleUpdateRule(rule.id, {
                            status:
                              e.target.value === "active"
                                ? AccessRuleStatus.ACTIVE
                                : e.target.value === "inactive"
                                  ? AccessRuleStatus.INACTIVE
                                  : AccessRuleStatus.EXPORT_SHARE
                          })
                        }
                        className="rounded-lg border px-3 py-2 text-sm"
                      >
                        <option value="active">فعال</option>
                        <option value="inactive">غیرفعال</option>
                        <option value="export-share">
                          محدودیت خروجی و اشتراک گذاری
                        </option>
                      </select>
                    </div>

                    {/* Action Type */}
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">عمل:</label>
                      <select
                        value={rule.action}
                        onChange={(e) =>
                          handleUpdateRule(rule.id, {
                            action: e.target.value as AccessAction
                          })
                        }
                        className="rounded-lg border px-3 py-2 text-sm"
                      >
                        <option value={AccessAction.SHOW}>نمایش</option>
                        <option value={AccessAction.HIDE}>مخفی</option>
                      </select>
                    </div>

                    {/* Tabs: Role-based vs Query-based */}
                    <div className="border-t pt-4">
                      <label className="mb-3 block text-sm font-medium">
                        شرط اعمال قانون:
                      </label>
                      <Tabs
                        value={rule.mode}
                        onValueChange={(value) =>
                          handleUpdateRule(rule.id, {
                            mode: value as AccessRuleMode
                          })
                        }
                        dir="rtl"
                      >
                        <TabsList className="w-full rounded-lg bg-slate-100 p-1">
                          <TabsTrigger
                            value={AccessRuleMode.ROLE}
                            className="flex-1 rounded-md data-[state=active]:bg-white"
                          >
                            براساس نقش
                          </TabsTrigger>
                          <TabsTrigger
                            value={AccessRuleMode.USER}
                            className="flex-1 rounded-md data-[state=active]:bg-white"
                          >
                            براساس کاربر
                          </TabsTrigger>
                          <TabsTrigger
                            value={AccessRuleMode.QUERY}
                            className="flex-1 rounded-md data-[state=active]:bg-white"
                          >
                            براساس کوئری
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent
                          value={AccessRuleMode.ROLE}
                          className="pt-4"
                        >
                          <div className="space-y-3">
                            <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
                              <span className="text-sm font-medium">همه</span>
                              <Checkbox
                                checked={rule.allRoles}
                                onCheckedChange={(checked) =>
                                  handleUpdateRule(rule.id, {
                                    allRoles: checked === true
                                  })
                                }
                              />
                            </label>

                            <div className="flex items-center justify-between gap-3">
                              <label className="text-sm font-medium">
                                نقش‌ها
                              </label>
                              <span className="text-xs text-slate-500">
                                {rule.allRoles
                                  ? "همه نقش‌ها"
                                  : rule.roleIds.length
                                    ? `${rule.roleIds.length} مورد انتخاب شده`
                                    : "بدون انتخاب"}
                              </span>
                            </div>

                            <Input
                              value={roleSearch}
                              onChange={(e) => setRoleSearch(e.target.value)}
                              placeholder="جستجو نقش..."
                              className="text-right"
                              disabled={rule.allRoles}
                            />

                            <ScrollArea
                              className={`h-56 rounded-lg border border-slate-200 bg-white p-3 ${
                                rule.allRoles
                                  ? "pointer-events-none opacity-60"
                                  : ""
                              }`}
                            >
                              <div className="space-y-2">
                                {isRolesLoading ? (
                                  <div className="py-6 text-center text-sm text-slate-500">
                                    در حال بارگذاری نقش‌ها...
                                  </div>
                                ) : filteredRoles.length ? (
                                  filteredRoles.map((role) => {
                                    const labelFa =
                                      role.displayNameFa ?? role.nameFa;
                                    const isChecked = rule.roleIds.includes(
                                      role.id
                                    );
                                    return (
                                      <label
                                        key={role.id}
                                        className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 px-3 py-2 transition-colors hover:border-primary/60 hover:bg-primary/5"
                                      >
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={() =>
                                            toggleRoleForRule(rule.id, role.id)
                                          }
                                          className="mt-0.5"
                                        />
                                        <span className="flex-1 text-sm">
                                          <span className="block font-medium text-slate-700">
                                            {labelFa}
                                          </span>
                                          {role.description && (
                                            <span className="mt-1 block text-xs text-slate-500">
                                              {role.description}
                                            </span>
                                          )}
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="py-6 text-center text-sm text-slate-500">
                                    نقشی یافت نشد
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        </TabsContent>

                        <TabsContent
                          value={AccessRuleMode.USER}
                          className="pt-4"
                        >
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <label className="text-sm font-medium">
                                کاربران
                              </label>
                              <span className="text-xs text-slate-500">
                                {rule.userIds.length
                                  ? `${rule.userIds.length} مورد انتخاب شده`
                                  : "بدون انتخاب"}
                              </span>
                            </div>

                            <Input
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              placeholder="جستجو کاربر..."
                              className="text-right"
                            />

                            <ScrollArea className="h-56 rounded-lg border border-slate-200 bg-white p-3">
                              <div className="space-y-2">
                                {isUsersLoading ? (
                                  <div className="py-6 text-center text-sm text-slate-500">
                                    در حال بارگذاری کاربران...
                                  </div>
                                ) : users.length ? (
                                  users.map((user) => {
                                    const isChecked = rule.userIds.includes(
                                      user.userId
                                    );

                                    return (
                                      <label
                                        key={user.userId}
                                        className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 px-3 py-2 transition-colors hover:border-primary/60 hover:bg-primary/5"
                                      >
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={() =>
                                            setConfig((prev) => {
                                              const ruleItem =
                                                TableAccessManager.getRule(
                                                  prev,
                                                  rule.id
                                                );
                                              if (!ruleItem) return prev;

                                              return TableAccessManager.updateRule(
                                                prev,
                                                rule.id,
                                                {
                                                  userIds: togglePrimitive(
                                                    ruleItem.userIds,
                                                    user.userId
                                                  )
                                                }
                                              );
                                            })
                                          }
                                          className="mt-0.5"
                                        />
                                        <span className="flex-1 text-sm">
                                          <span className="block font-medium text-slate-700">
                                            {resolveUserLabel(user)}
                                          </span>
                                          <span className="mt-1 block text-xs text-slate-500">
                                            {user.userName}
                                            {user.roleNameFa
                                              ? ` - ${user.roleNameFa}`
                                              : ""}
                                          </span>
                                        </span>
                                      </label>
                                    );
                                  })
                                ) : (
                                  <div className="py-6 text-center text-sm text-slate-500">
                                    کاربری یافت نشد
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        </TabsContent>

                        <TabsContent
                          value={AccessRuleMode.QUERY}
                          className="pt-4"
                        >
                          <div className="space-y-3">
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                              کوئری باید خروجی{" "}
                              <span className="font-mono">true/false</span>{" "}
                              داشته باشد. وقتی
                              <span className="font-mono"> true </span>
                              شد، عمل «نمایش/مخفی» روی {applyTargetLabel} اعمال
                              می‌شود.
                              <div className="mt-1 font-mono">
                                مثال: roleId == 3 && userId != 0
                              </div>
                            </div>

                            <Textarea
                              value={rule.query}
                              onChange={(e) =>
                                handleUpdateRule(rule.id, {
                                  query: e.target.value
                                })
                              }
                              className="min-h-[120px] font-mono text-sm"
                              placeholder="کوئری را وارد کنید"
                            />
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>

                    {/* Columns Section */}
                    {shouldShowColumns && (
                      <div className="border-t pt-4">
                        <label className="mb-3 block text-sm font-medium">
                          {columnsTitle ?? "ستون‌های قابل دسترسی:"}
                        </label>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs text-slate-500">
                            {rule.columns.length
                              ? `${rule.columns.length} ستون انتخاب شده`
                              : "بدون انتخاب"}
                          </span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button type="button" variant="outline" size="sm">
                                انتخاب ستون‌ها
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-[360px] space-y-3"
                              align="end"
                            >
                              <div className="space-y-2">
                                <div className="text-xs font-medium text-slate-600">
                                  ستون‌های دیتابیسی و دستی
                                </div>
                                <Input
                                  value={columnSearch}
                                  onChange={(e) =>
                                    setColumnSearch(e.target.value)
                                  }
                                  placeholder="جستجو ستون..."
                                  className="text-right"
                                />
                              </div>

                              <ScrollArea className="h-64 rounded-lg border border-slate-200 bg-white p-3">
                                <div className="space-y-2">
                                  {filteredColumns.length ? (
                                    filteredColumns.map((col) => {
                                      const isChecked =
                                        rule.columns.includes(col);
                                      return (
                                        <label
                                          key={col}
                                          className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 transition-colors hover:border-primary/60 hover:bg-primary/5"
                                        >
                                          <span className="flex-1 text-right text-sm text-slate-700">
                                            {resolveColumnLabel(col)}
                                          </span>
                                          <Checkbox
                                            checked={isChecked}
                                            onCheckedChange={() =>
                                              toggleColumnForRule(rule.id, col)
                                            }
                                          />
                                        </label>
                                      );
                                    })
                                  ) : (
                                    <div className="py-6 text-center text-sm text-slate-500">
                                      ستونی یافت نشد
                                    </div>
                                  )}
                                </div>
                              </ScrollArea>
                            </PopoverContent>
                          </Popover>
                        </div>

                        {/* Selected Columns */}
                        {rule.columns.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {rule.columns.map((col) => (
                              <div
                                key={col}
                                className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-slate-700"
                              >
                                <span className="text-sm">
                                  {resolveColumnLabel(col)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleColumnForRule(rule.id, col)
                                  }
                                  className="hover:text-slate-900"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Validation Message */}
                    {!TableAccessManager.isValidRule(
                      rule,
                      shouldShowColumns
                    ) && (
                      <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                        ⚠️ لطفاً تمام فیلدهای ضروری را پر کنید
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {config.rules.length === 0 && (
              <div className="py-8 text-center text-gray-400">
                هنوز قانونی تعریف نشده. برای شروع روی دکمه فوق کلیک کنید.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            لغو
          </Button>
          <Button onClick={saveConfiguration}>
            <span>ذخیره تنظیمات</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
